/**
 * Convert Lead Modal Component
 * Modal for converting a lead to an enrolled student
 */

import React, { useState } from 'react';
import { X, GraduationCap, User, Mail, Phone, Calendar, CheckCircle2, BookOpen } from 'lucide-react';

const ConvertLeadModal = ({ isOpen, onClose, onSubmit, isLoading, lead, classes = [] }) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    // Student info
    firstName: lead?.childFirstName || '',
    lastName: lead?.childLastName || '',
    dateOfBirth: '',
    gender: '',
    
    // Parent/Guardian info (from lead)
    parentFirstName: lead?.parentFirstName || '',
    parentLastName: lead?.parentLastName || '',
    parentEmail: lead?.email || '',
    parentPhone: lead?.phone || '',
    relationship: 'parent',
    
    // Enrollment info
    classId: '',
    startDate: '',
    notes: '',
    
    // Options
    createParentAccount: true,
    sendWelcomeEmail: true,
  });

  if (!isOpen || !lead) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleClose = () => {
    setStep(1);
    setFormData({
      firstName: lead?.childFirstName || '',
      lastName: lead?.childLastName || '',
      dateOfBirth: '',
      gender: '',
      parentFirstName: lead?.parentFirstName || '',
      parentLastName: lead?.parentLastName || '',
      parentEmail: lead?.email || '',
      parentPhone: lead?.phone || '',
      relationship: 'parent',
      classId: '',
      startDate: '',
      notes: '',
      createParentAccount: true,
      sendWelcomeEmail: true,
    });
    onClose();
  };

  const canProceed = () => {
    if (step === 1) {
      return formData.firstName && formData.lastName;
    }
    if (step === 2) {
      return formData.parentFirstName && formData.parentLastName && formData.parentEmail;
    }
    return true;
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-6">
      <div className="flex items-center">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-medium ${
          step >= 1 ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-500'
        }`}>
          1
        </div>
        <div className={`w-12 h-1 ${step >= 2 ? 'bg-purple-600' : 'bg-gray-200'}`} />
        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-medium ${
          step >= 2 ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-500'
        }`}>
          2
        </div>
        <div className={`w-12 h-1 ${step >= 3 ? 'bg-purple-600' : 'bg-gray-200'}`} />
        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-medium ${
          step >= 3 ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-500'
        }`}>
          3
        </div>
      </div>
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-4">
      <div className="bg-green-50 rounded-lg p-4 mb-6">
        <h4 className="font-medium text-green-900 mb-2 flex items-center">
          <GraduationCap size={18} className="mr-2" />
          Lead Information
        </h4>
        <div className="space-y-1 text-sm">
          <p className="text-gray-700">
            <span className="font-medium">Lead:</span> {lead.parentFirstName} {lead.parentLastName}
          </p>
          <p className="text-gray-700">
            <span className="font-medium">Child:</span> {lead.childFirstName} {lead.childLastName}
          </p>
          {lead.programInterest && (
            <p className="text-gray-700">
              <span className="font-medium">Interest:</span> {lead.programInterest}
            </p>
          )}
        </div>
      </div>

      <h4 className="font-medium text-gray-900 mb-3">Student Information</h4>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="form-label">First Name *</label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              className="form-input pl-10"
              value={formData.firstName}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              required
            />
          </div>
        </div>
        <div>
          <label className="form-label">Last Name *</label>
          <input
            type="text"
            className="form-input"
            value={formData.lastName}
            onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
            required
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="form-label">Date of Birth</label>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="date"
              className="form-input pl-10"
              value={formData.dateOfBirth}
              onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
            />
          </div>
        </div>
        <div>
          <label className="form-label">Gender</label>
          <select
            className="form-input"
            value={formData.gender}
            onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
          >
            <option value="">Select...</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
            <option value="prefer_not_to_say">Prefer not to say</option>
          </select>
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-4">
      <h4 className="font-medium text-gray-900 mb-3">Parent/Guardian Information</h4>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="form-label">First Name *</label>
          <input
            type="text"
            className="form-input"
            value={formData.parentFirstName}
            onChange={(e) => setFormData({ ...formData, parentFirstName: e.target.value })}
            required
          />
        </div>
        <div>
          <label className="form-label">Last Name *</label>
          <input
            type="text"
            className="form-input"
            value={formData.parentLastName}
            onChange={(e) => setFormData({ ...formData, parentLastName: e.target.value })}
            required
          />
        </div>
      </div>

      <div>
        <label className="form-label">Email *</label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="email"
            className="form-input pl-10"
            value={formData.parentEmail}
            onChange={(e) => setFormData({ ...formData, parentEmail: e.target.value })}
            required
          />
        </div>
      </div>

      <div>
        <label className="form-label">Phone</label>
        <div className="relative">
          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="tel"
            className="form-input pl-10"
            value={formData.parentPhone}
            onChange={(e) => setFormData({ ...formData, parentPhone: e.target.value })}
          />
        </div>
      </div>

      <div>
        <label className="form-label">Relationship</label>
        <select
          className="form-input"
          value={formData.relationship}
          onChange={(e) => setFormData({ ...formData, relationship: e.target.value })}
        >
          <option value="parent">Parent</option>
          <option value="guardian">Guardian</option>
          <option value="grandparent">Grandparent</option>
          <option value="other">Other</option>
        </select>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-4">
      <h4 className="font-medium text-gray-900 mb-3">Enrollment Information</h4>
      
      {classes.length > 0 && (
        <div>
          <label className="form-label">
            <BookOpen size={14} className="inline mr-1" />
            Enroll in Class
          </label>
          <select
            className="form-input"
            value={formData.classId}
            onChange={(e) => setFormData({ ...formData, classId: e.target.value })}
          >
            <option value="">Select a class (optional)</option>
            {classes.map((cls) => (
              <option key={cls.id} value={cls.id}>
                {cls.name} - {cls.schedule || 'No schedule set'}
              </option>
            ))}
          </select>
        </div>
      )}

      {formData.classId && (
        <div>
          <label className="form-label">
            <Calendar size={14} className="inline mr-1" />
            Start Date
          </label>
          <input
            type="date"
            className="form-input"
            value={formData.startDate}
            onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
          />
        </div>
      )}

      <div>
        <label className="form-label">Notes</label>
        <textarea
          className="form-input"
          rows="3"
          placeholder="Any additional notes about the enrollment..."
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
        />
      </div>

      <div className="space-y-3 pt-4 border-t">
        <label className="flex items-center space-x-3">
          <input
            type="checkbox"
            checked={formData.createParentAccount}
            onChange={(e) => setFormData({ ...formData, createParentAccount: e.target.checked })}
            className="w-4 h-4 text-purple-600 rounded"
          />
          <span className="text-gray-700">Create parent portal account</span>
        </label>
        <label className="flex items-center space-x-3">
          <input
            type="checkbox"
            checked={formData.sendWelcomeEmail}
            onChange={(e) => setFormData({ ...formData, sendWelcomeEmail: e.target.checked })}
            className="w-4 h-4 text-purple-600 rounded"
          />
          <span className="text-gray-700">Send welcome email to parent</span>
        </label>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
              <GraduationCap size={20} className="text-green-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Convert Lead</h2>
              <p className="text-sm text-gray-500">Convert this lead to an enrolled student</p>
            </div>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {renderStepIndicator()}
          <form id="convert-form" onSubmit={handleSubmit}>
            {step === 1 && renderStep1()}
            {step === 2 && renderStep2()}
            {step === 3 && renderStep3()}
          </form>
        </div>

        {/* Footer */}
        <div className="flex justify-between space-x-3 p-6 border-t border-gray-100">
          {step === 1 ? (
            <>
              <button
                type="button"
                onClick={handleClose}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => setStep(2)}
                disabled={!canProceed()}
                className="btn-primary disabled:opacity-50"
              >
                Continue
              </button>
            </>
          ) : step === 2 ? (
            <>
              <button
                type="button"
                onClick={() => setStep(1)}
                className="btn-secondary"
              >
                Back
              </button>
              <button
                type="button"
                onClick={() => setStep(3)}
                disabled={!canProceed()}
                className="btn-primary disabled:opacity-50"
              >
                Continue
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setStep(2)}
                className="btn-secondary"
              >
                Back
              </button>
              <button
                type="submit"
                form="convert-form"
                disabled={isLoading || !canProceed()}
                className="btn-primary disabled:opacity-50 flex items-center space-x-2 bg-green-600 hover:bg-green-700"
              >
                <CheckCircle2 size={18} />
                <span>{isLoading ? 'Converting...' : 'Convert to Student'}</span>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConvertLeadModal;
